import { useApp } from '../App';
import Tutorial from '../components/Tutorial';
import VoiceButton from '../components/VoiceButton';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import { useI18n } from '../i18n/index.jsx';
import { useState } from 'react';
import api from '../api';
import { COUNTRIES } from './CountrySelect';

export default function Dashboard() {
  const { user, setPage, isAdmin, currentCompany, hasWorkAuth, hasContract, companies, isSuperuser, testMode, toggleTestMode, productAccess } = useApp();
  const { t } = useI18n();
  const [voiceResult, setVoiceResult] = useState(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    finance: true,
    documents: true,
    tools: false,
    admin: false,
  });

  const KYC_DISABLED = true; // TEMP: KYC disabled globally

  async function startKyc() {
    try {
      setKycLoading(true);
      const { data } = await api.get('/kyc/session');
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      alert(e.userMessage || 'KYC недоступен');
    } finally {
      setKycLoading(false);
    }
  }

  const selectedCountry = localStorage.getItem('selected_country') || 'PL';
  const countryInfo = COUNTRIES.find(c => c.code === selectedCountry) || COUNTRIES[0];

  const isNew = user.profile_status === 'draft' || !currentCompany;

  const completedSteps = [];
  if (user.first_name && user.last_name) completedSteps.push('profile');
  if (user.passport_number) completedSteps.push('passport');
  if (currentCompany) completedSteps.push('company');
  if (hasContract) completedSteps.push('contract');

  function handleVoice(text, error) {
    if (error) { setVoiceResult({ error }); return; }
    setVoiceResult({ text });
    const lower = text.toLowerCase();
    if (lower.includes('фактур')) setPage('invoices_out');
    else if (lower.includes('касс')) setPage('cash');
    else if (lower.includes('заявк') || lower.includes('освядч')) setPage('applications');
    else if (lower.includes('карт') && lower.includes('побыт')) setPage('karta_pobytu');
    else if (lower.includes('профил')) setPage('profile');
    else if (lower.includes('документ') || lower.includes('паспорт')) setPage('documents');
    else if (lower.includes('вывод')) setPage('withdrawals');
    else if (lower.includes('демо')) setPage('demo');
  }

  function toggleSection(section) {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }

  // ══════ MINIMAL MENU — 3 sections only ══════
  const isCompanyOwner = user?.account_type === 'own_company' || user?.role === 'company_owner' || user?.role === 'superadmin' || user?._original_role === 'superadmin' || (productAccess?.accounting && !productAccess?.incubator && !productAccess?.genro);
  const isGenroOnly = !!(productAccess?.genro && !productAccess?.incubator && !productAccess?.accounting);
  const isCombo = !!(productAccess?.genro && productAccess?.incubator && productAccess?.accounting);
  
  // Simplified menu for incubator users — focus on proformas
  const menuCategories = isGenroOnly ? [
    {
      id: 'documents',
      title: '📋 ' + t('dashboard.cat_documents'),
      color: '#6366f1',
      items: [
        { id: 'work_permit', icon: '📋', title: t('dashboard.menu_work_permit'), desc: t('dashboard.menu_work_permit_desc') },
        { id: 'karta_pobytu', icon: '🏠', title: t('dashboard.menu_karta_pobytu'), desc: t('dashboard.menu_karta_pobytu_desc') },
        { id: 'a1_application', icon: '🌍', title: 'A1', desc: 'A1 application' },
      ]
    },
    {
      id: 'tools',
      title: '🛠 ' + t('dashboard.cat_tools'),
      color: '#8b5cf6',
      items: [
        { id: 'support', icon: '📞', title: t('dashboard.menu_support'), desc: t('dashboard.menu_support_desc') },
        { id: 'profile', icon: '👤', title: t('dashboard.menu_profile'), desc: t('dashboard.menu_profile_desc') },
      ]
    },
  ] : isCompanyOwner ? [
    {
      id: 'finance',
      title: '💰 ' + t('dashboard.cat_finance'),
      color: '#f59e0b',
      items: [
        { id: 'my_finance', icon: '🏦', title: t('dashboard.menu_my_finance'), desc: t('dashboard.menu_my_finance_desc') },
        { id: 'invoices_out', icon: '📄', title: t('dashboard.menu_invoices'), desc: t('dashboard.menu_invoices_desc') },
        { id: 'my_contracts', icon: '📑', title: t('dashboard.menu_contracts'), desc: t('dashboard.menu_contracts_desc') },
        { id: 'my_taxes', icon: '🧮', title: t('dashboard.menu_taxes'), desc: t('dashboard.menu_taxes_desc') },
        { id: 'payment_links', icon: '💳', title: t('dashboard.menu_payment_links'), desc: t('dashboard.menu_payment_links_desc') },
        { id: 'top_up', icon: '📥', title: t('dashboard.menu_top_up'), desc: t('dashboard.menu_top_up_desc') },
        { id: 'withdrawals', icon: '📤', title: t('dashboard.menu_withdraw'), desc: t('dashboard.menu_withdraw_desc') },
      ]
    },
    {
      id: 'documents',
      title: '📋 ' + t('dashboard.cat_documents'),
      color: '#6366f1',
      items: [
        { id: 'work_permit', icon: '📋', title: t('dashboard.menu_work_permit'), desc: t('dashboard.menu_work_permit_desc') },
        { id: 'karta_pobytu', icon: '🏠', title: t('dashboard.menu_karta_pobytu'), desc: t('dashboard.menu_karta_pobytu_desc') },
        { id: 'my_contracts', icon: '📝', title: t('dashboard.menu_my_contracts'), desc: t('dashboard.menu_my_contracts_desc') },
        { id: 'counterparties', icon: '🤝', title: t('dashboard.menu_counterparties'), desc: t('dashboard.menu_counterparties_desc') },
        { id: 'contract_templates', icon: '📑', title: t('dashboard.menu_templates'), desc: t('dashboard.menu_templates_desc') },
      ]
    },
    {
      id: 'tools',
      title: '🛠 ' + t('dashboard.cat_tools'),
      color: '#8b5cf6',
      items: [
        { id: 'calendar', icon: '📅', title: t('dashboard.menu_calendar'), desc: t('dashboard.menu_calendar_desc') },
        { id: 'support', icon: '📞', title: t('dashboard.menu_support'), desc: t('dashboard.menu_support_desc') },
        { id: 'notifications', icon: '🔔', title: t('dashboard.menu_notifications'), desc: t('dashboard.menu_notifications_desc') },
        { id: 'security', icon: '🔐', title: t('dashboard.menu_security'), desc: t('dashboard.menu_security_desc') },
        { id: 'ai_accountant', icon: '🤖', title: t('dashboard.menu_ai'), desc: t('dashboard.menu_ai_desc') },
      ]
    },
  ] : [
    // ─── INCUBATOR MENU (simplified) ───
    {
      id: 'main',
      title: '📄 ' + t('dashboard.cat_proformas'),
      color: '#6366f1',
      items: [
        ...(!hasContract ? [{ id: 'gateway_sign', icon: '🚀', title: t('dashboard.menu_gateway'), desc: t('dashboard.menu_gateway_desc'), highlight: true }] : []),
        { id: 'invoices_out', icon: '📄', title: t('dashboard.menu_invoices'), desc: t('dashboard.menu_invoices_desc') },
        { id: 'invoices_in', icon: '📸', title: t('dashboard.menu_invoices_in'), desc: t('dashboard.menu_invoices_in_desc') },
        { id: 'my_finance', icon: '🏦', title: t('dashboard.menu_my_finance'), desc: t('dashboard.menu_my_finance_desc') },
        ...(isCombo ? [{ id: 'my_taxes', icon: '🧮', title: t('dashboard.menu_taxes'), desc: t('dashboard.menu_taxes_desc') }] : []),
        { id: 'payment_links', icon: '💳', title: t('dashboard.menu_payment_links'), desc: t('dashboard.menu_payment_links_desc') },
        { id: 'withdrawals', icon: '💸', title: t('dashboard.menu_withdraw'), desc: hasContract ? t('dashboard.menu_withdraw_desc') : t('dashboard.menu_withdraw_need_contract') },
        { id: 'my_contracts', icon: '📑', title: t('dashboard.menu_contracts'), desc: t('dashboard.menu_contracts_desc') },
      ]
    },
    {
      id: 'documents',
      title: '📋 ' + t('dashboard.cat_documents'),
      color: '#f59e0b',
      items: [
        { id: 'work_permit', icon: '📋', title: t('dashboard.menu_work_permit'), desc: t('dashboard.menu_work_permit_desc') },
        { id: 'karta_pobytu', icon: '🏠', title: t('dashboard.menu_karta_pobytu'), desc: t('dashboard.menu_karta_pobytu_desc') },
      ]
    },
    {
      id: 'tools',
      title: '🛠 ' + t('dashboard.cat_tools'),
      color: '#8b5cf6',
      items: [
        { id: 'support', icon: '📞', title: t('dashboard.menu_support'), desc: t('dashboard.menu_support_desc') },
        { id: 'ai_accountant', icon: '🤖', title: t('dashboard.menu_ai'), desc: t('dashboard.menu_ai_desc') },
        { id: 'profile', icon: '👤', title: t('dashboard.menu_profile'), desc: t('dashboard.menu_profile_desc') },
      ]
    },
  ];

  // Genro is available only as explicit integration entry (not inside primary Workify flows)
  if (productAccess?.genro) {
    const toolsCategory = menuCategories.find((c) => c.id === 'tools');
    if (toolsCategory && !toolsCategory.items.some((i) => i.id === 'genro_entry')) {
      toolsCategory.items.unshift({
        id: 'genro_entry',
        icon: '🧠',
        title: 'Open in Genro AI',
        desc: 'Content creation in Genro AI',
      });
    }
  }

  const adminCategory = {
    id: 'admin',
    title: '⚙️ ' + t('dashboard.cat_admin'),
    color: '#ef4444',
    items: [
      { id: 'admin_users', icon: '👥', title: t('dashboard.admin_users'), desc: t('dashboard.admin_users_desc') },
      { id: 'admin_companies', icon: '🏢', title: t('dashboard.admin_companies'), desc: t('dashboard.admin_companies_desc') },
      { id: 'admin_tier2', icon: '🏗️', title: t('dashboard.admin_tier2'), desc: t('dashboard.admin_tier2_desc') },
      { id: 'admin_requests', icon: '📋', title: t('dashboard.admin_requests'), desc: t('dashboard.admin_requests_desc') },
      { id: 'admin_contracts', icon: '📑', title: t('dashboard.admin_contracts'), desc: t('dashboard.admin_contracts_desc') },
      { id: 'admin_invoices', icon: '🧾', title: t('dashboard.admin_invoices'), desc: t('dashboard.admin_invoices_desc') },
      { id: 'admin_payouts', icon: '💸', title: t('dashboard.admin_payouts'), desc: t('dashboard.admin_payouts_desc') },
      { id: 'admin_gateway_payments', icon: '💳', title: t('dashboard.admin_payments'), desc: t('dashboard.admin_payments_desc') },
      { id: 'admin_accounting', icon: '📊', title: t('dashboard.admin_accounting'), desc: t('dashboard.admin_accounting_desc') },
      { id: 'admin_bank_import', icon: '🏦', title: t('dashboard.admin_bank_import'), desc: t('dashboard.admin_bank_import_desc') },
      { id: 'tax_dashboard', icon: '🏦', title: t('dashboard.admin_tax_dashboard'), desc: t('dashboard.admin_tax_dashboard_desc') },
      { id: 'admin_support', icon: '📞', title: t('dashboard.admin_support'), desc: t('dashboard.admin_support_desc') },
      { id: 'admin_broadcast', icon: '📢', title: t('dashboard.admin_broadcast'), desc: t('dashboard.admin_broadcast_desc') },
      { id: 'admin_audit', icon: '📋', title: t('dashboard.admin_audit'), desc: t('dashboard.admin_audit_desc') },
      { id: 'admin_analytics', icon: '📈', title: t('dashboard.admin_analytics'), desc: t('dashboard.admin_analytics_desc') },
      { id: 'admin_diagnostic', icon: '🔧', title: t('dashboard.admin_diagnostic'), desc: t('dashboard.admin_diagnostic_desc') },
      { id: 'admin_diagnostic', icon: '🔧', title: t('dashboard.admin_diagnostic'), desc: t('dashboard.admin_diagnostic_desc') },
    ]
  };

  // Tier 2: Own Company — HR & Team management
  if (isCompanyOwner) {
    menuCategories.splice(1, 0, {
      id: 'hr',
      title: '👥 ' + t('dashboard.cat_hr'),
      color: '#10b981',
      items: [
        { id: 'hr_employees', icon: '👥', title: t('dashboard.menu_employees'), desc: t('dashboard.menu_employees_desc') },
        { id: 'company_team', icon: '🔑', title: t('dashboard.menu_team'), desc: t('dashboard.menu_team_desc') },
        { id: 'counterparties', icon: '🤝', title: t('dashboard.menu_counterparties'), desc: t('dashboard.menu_counterparties_desc') },
        { id: 'calendar', icon: '📅', title: t('dashboard.menu_calendar'), desc: t('dashboard.menu_calendar_desc') },
      ],
    });
  }

  if (isAdmin) {
    menuCategories.push(adminCategory);
  }

  return (
    <div className="dashboard">
      {!KYC_DISABLED && user?.has_paid_access && user?.profile_status !== 'verified' && (
        <div style={{
          background: 'rgba(239,68,68,0.15)',
          border: '1px solid rgba(239,68,68,0.4)',
          padding: 12,
          borderRadius: 12,
          marginBottom: 12,
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: 6 }}>⚠️ {t('dashboard.kyc_required')}</div>
          <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 8 }}>
            {t('dashboard.kyc_desc')}
          </div>
          <button className="btn btn-primary" onClick={startKyc} disabled={kycLoading}>
            {kycLoading ? '⏳ ' + t('dashboard.kyc_opening') : '✅ ' + t('dashboard.kyc_pass')}
          </button>
        </div>
      )}
      <div className="gpu-guide-banner">
        <div>
          <strong>🔥 GPU-гайд 2026</strong>
          <span>Сдача GPU в аренду: бизнес-план, расчёты и окупаемость. 29.99€</span>
        </div>
        <a href="https://t.me/aistartlab_bot?start=gpu_guide" target="_blank" rel="noreferrer">Купить →</a>
      </div>

      <div className="dashboard-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1>👋 {user.first_name || t('dashboard.welcome')}</h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <ThemeToggle />
            <LanguageSelector compact />
            <VoiceButton onResult={handleVoice} style={{ width: 44, height: 44, borderRadius: '50%', padding: 0, fontSize: 20 }} />
          </div>
        </div>

        {/* Test mode */}
        {isSuperuser && (
          <div style={{
            background: testMode ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.05)',
            borderRadius: 8, padding: 8, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: 12 }}>🧪 {t('common.test_mode')} {testMode ? t('common.test_mode_on') : t('common.test_mode_off')}</span>
            <button onClick={toggleTestMode} className="btn btn-small" style={{ padding: '4px 8px', fontSize: 11 }}>
              {testMode ? t('common.disable') : t('common.enable')}
            </button>
          </div>
        )}
      </div>

      {/* Categorized Menu */}
      {menuCategories.map(category => (
        <div key={category.id} style={{ marginBottom: 16 }}>
          {/* Category Header */}
          <div
            onClick={() => toggleSection(category.id)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              background: `linear-gradient(135deg, ${category.color}22 0%, ${category.color}11 100%)`,
              borderRadius: 12,
              cursor: 'pointer',
              borderLeft: `4px solid ${category.color}`,
              marginBottom: expandedSections[category.id] ? 8 : 0,
            }}
          >
            <span style={{ fontWeight: 'bold' }}>{category.title}</span>
            <span style={{ 
              transform: expandedSections[category.id] ? 'rotate(180deg)' : 'rotate(0)', 
              transition: 'transform 0.2s',
              opacity: 0.6,
            }}>
              ▼
            </span>
          </div>

          {/* Category Items */}
          {expandedSections[category.id] && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 10,
            }}>
              {category.items.map(card => (
                <div
                  key={card.id}
                  onClick={() => setPage(card.id)}
                  style={{
                    background: card.highlight
                      ? 'linear-gradient(135deg, rgba(16,185,129,0.25) 0%, rgba(5,150,105,0.15) 100%)'
                      : 'rgba(255,255,255,0.05)',
                    borderRadius: 14,
                    padding: '16px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: 'pointer',
                    border: card.highlight ? '1px solid rgba(16,185,129,0.4)' : '1px solid rgba(255,255,255,0.06)',
                    transition: 'transform 0.1s',
                  }}
                >
                  <div style={{ fontSize: 30, flexShrink: 0 }}>{card.icon}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{card.title}</div>
                    <div style={{ fontSize: 11, opacity: 0.5, marginTop: 2 }}>{card.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
